import React, { useState, useEffect, useContext, useRef } from "react";

import { get, set, del } from "idb-keyval";

// we shim an implementation of "next/dynamic" in DS9 in webpack
// we HAVE to use magic comments https://loadable-components.com/docs/babel-plugin/#magic-comments
import dynamic from "next/dynamic";
import Header from "./Header";
import Retry from "Utilities/Retry";
import Loader from "./Loader/Loader";
import IsClutchLoaded from "./Head/Scripts/IsClutchLoaded";
import useWindow from "Hooks/useWindow";
import initializeGoogleAnalytics from "Utilities/initializeGoogleAnalytics";
import { onINP, onCLS } from "web-vitals/attribution";

import LayoutProvider from "Contexts/LayoutProvider";
import ErrorBoundary, {
  WSODErrorBoundary,
} from "UtilityComponents/ErrorBoundary";
import Event from "Utilities/Event";
import DeferredLoadingWrapper from "Components/Transitions/DeferredLoadingWrapper/DeferredLoadingWrapper";

import Segment from "./Head/Scripts/Segment";
import Tracking from "./Head/Scripts/Tracking";
import SeoClarityScripts from "./Next/seoClarityScripts"
import BloomreachPixelTracking from "./Head/Scripts/BloomreachPixelTracking";
import Miso from "./Head/Scripts/Miso/Miso";
import GoogleAnalytics4 from "./Head/Scripts/GoogleAnalytics4";
import GoogleAds from "./Head/Scripts/GoogleAds";
import Grin from "./Head/Scripts/SocialPlatforms/Grin";
import TikTok from "./Head/Scripts/SocialPlatforms/TikTok";
import CrispChat from "./Head/Scripts/CrispChat";
import OfflineStatus from "./OfflineStatus";
import { ThemeContext } from "Contexts/ThemeContext";
import { UserContext } from "Contexts/UserContext/UserContext";
import FeatureFlagWrapper from 'UtilityComponents/FeatureFlagWrapper'
import LoyaltySignUpModal from "./LoyaltySignUpModal"

import KitDetailsDrawer from "./drawers/kitDetailsDrawer";
import AddToCartDrawer from "./drawers/addToCartDrawer";
import ListDrawer from "./drawers/listDrawer";
import EmailSignUpModal from "./EmailSignUpModal";
import LoyaltyNonBetaModal from "./LoyaltyNonBetaModal";
import SsrScripts from "./Next/SsrScripts";
import VariantDetailsDrawer from "./drawers/VariantDetailsDrawer";
import useINPLogger from "../Hooks/CoreWebVitals/INPLogger";
import useCLSLogger from "../Hooks/CoreWebVitals/CLSLogger";
import IosInstallPrompt from "../Organisms/IosInstallPrompt/IosInstallPrompt";
import NavigatingProgressBar from "../Atoms/NavigatingProgressBar/NavigatingProgressBar";

const Layout = ({ state, children, reserveBannerSpace = false, crispEnabled = true, seoClarityEnabled = true, dontAutoSendClarityInitEvent = false, cwvEventsEnabled = true }) => {
  const renderCounter = useRef(0);
  renderCounter.current = renderCounter.current + 1;
  console.debug(`Layout Renders: ${renderCounter.current}`);

  const [isCondensedFooter, setIsCondensedFooter] = useState(false);
  const stateObject = JSON.parse(state);



  useWindow((window) => {
    //this puts indexdb functions on the windows for tng to use
    window.indexGet = get;
    window.indexSet = set;
    window.indexDelete = del;
    if (window.location.href.includes("/buy")) setIsCondensedFooter(true);
    else if (window.location.href.includes("/emailsignup"))
      setIsCondensedFooter(true);

    window.chatOpenCallback = function () {
      Event({
        category: "Chat",
        action: "Clicked",
        label: "Chat",
      });
    };
    // Unregister Service Worker
    if (typeof window !== "undefined") {
      // If the browser doesn't support service workers, bail.
      if ("serviceWorker" in navigator === false) {
        return;
      }

      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }

    function perfObserver(list, observer) {
      try {
        window.smi_web_vitals = window.smi_web_vitals || {}

        list.getEntries().forEach((entry) => {
          if (entry?.entryType == "paint") {
            window.smi_web_vitals.FCP = entry.startTime
          }
          else if (entry?.entryType == "largest-contentful-paint") {
            window.smi_web_vitals.LCP = entry.startTime
          }
          else if (entry?.entryType == "layout-shift") {
            let shiftValueToAdd = 0
            if (!entry.hadRecentInput) shiftValueToAdd = entry.value
            window.smi_web_vitals.CLS = shiftValueToAdd + (window.smi_web_vitals.CLS || 0)
            if (window.smi_web_vitals.CLS > 0.1 && shiftValueToAdd > 0) {
              //log so we can have visibility in logrocket
              // console.log(`CLS threshold exceeded`)
              // console.log('CLS', window.smi_web_vitals.CLS)
            }
          }
        });
      }
      catch (err) {
        console.error(err)
      }
    }

    try {
      const observer = new PerformanceObserver(perfObserver);
      observer.observe({ entryTypes: ['paint', 'largest-contentful-paint', 'layout-shift'] });
    }
    catch (err) {
      console.error(err)
    }
  });

  const inpLogger = useINPLogger()
  const clsLogger = useCLSLogger()

  useWindow((window) => {
    if (cwvEventsEnabled) {
      onINP(inpLogger);
      onCLS(clsLogger);
    }
  })



  return (
    <>
    <LayoutProvider
      headerRedesignEnabled={stateObject.headerRedesignEnabled}
      stateObject={stateObject}
      services={stateObject.services}
    >
      <AppBody
        stateObject={stateObject}
        children={children}
        isCondensedFooter={isCondensedFooter}
        reserveBannerSpace={reserveBannerSpace}
        crispEnabled={crispEnabled}
        seoClarityEnabled={seoClarityEnabled}
        dontAutoSendClarityInitEvent={dontAutoSendClarityInitEvent}
      />
    </LayoutProvider>
    {/* Make sure this is always last */}
    <IsClutchLoaded />
    </>
  );
};

const AppBody = ({ stateObject, children, isCondensedFooter, reserveBannerSpace, crispEnabled, seoClarityEnabled, dontAutoSendClarityInitEvent }) => {
  const themeContext = useContext(ThemeContext);
  const { context: userContext } = useContext(UserContext);

  var html = stateObject.microFrontEndHtml;
  const currentTime = new Date();

  if (stateObject.headerRedesignEnabled) initializeGoogleAnalytics();

  return (
    <>
      <SsrScripts />
      <Loader isLoading={themeContext.isLoading} />
      <NavigatingProgressBar />
      {stateObject.headerRedesignEnabled && (
        <>
          <Header
            currentTime={currentTime}
            services={stateObject.services}
            user={stateObject.userGuid}
            reserveBannerSpace={reserveBannerSpace}
          >
            <div style={{ zIndex: "-1", minHeight: "75vh" }}>
              {children}
              {/* https://github.com/facebook/react/issues/10923#issuecomment-338715787 */}
              <div
                dangerouslySetInnerHTML={{ __html: html }}
                suppressHydrationWarning
              />

            </div>
          </Header>
          <KitDetailsDrawer />
          <AddToCartDrawer />
          <ListDrawer />
          <VariantDetailsDrawer />
        </>
      )}
      {/* we only have headerRedesignEnabled = false on checkout, it doesnt use new header */}
      {!stateObject.headerRedesignEnabled ? (
        <div style={{ zIndex: "-1" }}>
          {children}
          {/* https://github.com/facebook/react/issues/10923#issuecomment-338715787 */}
          <div
            dangerouslySetInnerHTML={{ __html: html }}
            suppressHydrationWarning
          />
        </div>
      ) : (
        <div style={{ zIndex: "-1" }} />
      )}
      <GoogleAnalytics4 />
      <GoogleAds />
      <Grin />
      <TikTok />
      <Segment />
      <Miso />
      <Tracking />
      <BloomreachPixelTracking />
      <ErrorBoundary>
        <DeferredLoadingWrapper
          loadPadding={"500px"}
          onLoad={() => {
            return dynamic(
              /* #__LOADABLE__ */() => Retry(() => import("./Footer"))
            );
          }}
          condensed={isCondensedFooter}
          currentTime={currentTime}
          isSignedUpForEmails={userContext?.isSignedUpForEmails}
          services={stateObject.services}
        />

        <EmailSignUpModal
          disabledPathNames={
            stateObject.featureFlags.chatWidgetAndEmailSignupDisabledPathNames
          }
        />
        <IosInstallPrompt />

        <FeatureFlagWrapper ff={"Disable_Loyalty_SignUp_Modals_on_Staging"} >
          <LoyaltySignUpModal />
          <LoyaltyNonBetaModal />
        </FeatureFlagWrapper>

      </ErrorBoundary>

      {crispEnabled ? <CrispChat /> : false}
      {seoClarityEnabled ? <SeoClarityScripts dontAutoSendClarityInitEvent={dontAutoSendClarityInitEvent} /> : false}

      <OfflineStatus />

    </>
  );
};

const LayoutWithErrorHandling = (props) => {
  return (
    <WSODErrorBoundary>
      <Layout {...props} />
    </WSODErrorBoundary>
  );
};

export default LayoutWithErrorHandling;
